import { ConnectionType } from '@wix/ambassador-premium-domains-v1-connected-domain/types';
import { Registrar } from '../../typing';
import { parseDomain } from '../../utils';
import { DOMAIN_CONNECTION_STATUSES } from '../../constants';
import { DomainConnectedStatus } from '../../typing/ConnectDomainTypes/connectedDomainSetupConfig';
/**
 * This file implements the `connectedDomainSetupConfig` query. The process begins by invoking the `getConnectedDomain` and `getConnectedDomainSetupInfo` functions.
 *
 * The implementation performs several checks:
 *  - It verifies if the domain is already taken.
 *  - It checks if the domain is connected to another site.
 *  - If the `connectionType` is `NAMESERVERS`, it ensures that there are nameserver records in the `connectedDomainSetupInfo`.
 *  - It checks if the subdomain is already connected to another site and if so returns connectionStatus Accordingly.
 *
 * If the `getConnectedDomain` function returns a `statusCode` of 404 (indicating the domain is not found), the following steps are executed:
 *  - The `fetchDomainSetupInfo` function is called.
 *  - The response is transformed. If the domain is a subdomain, the `connectionStatus` is set to `CAN_BE_CONNECTED` if there are `subdomainRecords`, otherwise it is set to `CREATE_CONNECT`.
 *  - The transformed `domainSetupInfoState` is returned.
 */
export const connectedDomainSetupConfig = async _ref => {
  let httpClient = _ref.httpClient,
    _ref$request = _ref.request,
    domain = _ref$request.domain,
    connectionType = _ref$request.connectionType,
    msid = _ref$request.msid,
    withRegistrar = _ref$request.withRegistrar;
  const _await$import$Domains = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
      httpClient
    }),
    getConnectedDomain = _await$import$Domains.getConnectedDomain,
    getConnectedDomainSetupInfo = _await$import$Domains.getConnectedDomainSetupInfo;
  const parsedDomain = domain.asciiDomain ? domain : parseDomain(domain);
  const fullAsciiDomain = parsedDomain.fullAsciiDomain;
  const isSubdomain = !!parsedDomain.subdomain;
  try {
    const _await$Promise$all = await Promise.all([getConnectedDomain({
        connectedDomainId: fullAsciiDomain
      }, {
        staleTime: 0
      }), withRegistrar ? getConnectedDomainSetupInfo({
        connectedDomainId: fullAsciiDomain
      }, {
        staleTime: 0
      }) : {
        connectedDomainSetupInfo: null
      }]),
      connectedDomain = _await$Promise$all[0].connectedDomain,
      connectedDomainSetupInfo = _await$Promise$all[1].connectedDomainSetupInfo;
    const _ref2 = connectedDomain ?? {},
      connectedDomainName = _ref2.domain,
      dnsPropagationStatus = _ref2.dnsPropagationStatus,
      siteInfo = _ref2.siteInfo,
      connectedConnectionType = _ref2.connectionType;
    const statusCode = DOMAIN_CONNECTION_STATUSES[dnsPropagationStatus] ?? {};
    const isConnectedToOtherSite = (siteInfo == null ? void 0 : siteInfo.id) !== msid;
    const domainSetupConfig = {
      connectionStatus: statusCode,
      connectedConnectionType: statusCode ? connectedConnectionType : null
    };
    if (connectedDomainName && withRegistrar) {
      var _subdomainRecords$cna, _pointingRecords$cnam, _pointingRecords$aRec, _nameserverRecord$nsR;
      const _ref3 = connectedDomainSetupInfo ?? {},
        nameserverRecord = _ref3.nameserverRecord,
        pointingRecords = _ref3.pointingRecords,
        subdomainRecords = _ref3.subdomainRecords,
        registrar = _ref3.registrar;
      if (!nameserverRecord && connectedConnectionType === ConnectionType.NAMESERVERS) {
        domainSetupConfig.connectionStatus = DomainConnectedStatus.CREATE_CONNECT;
      }
      if (isConnectedToOtherSite && isSubdomain) {
        domainSetupConfig.connectionStatus = DomainConnectedStatus.ALREADY_CONNECTED_TO_ANOTHER_SITE;
      }
      domainSetupConfig.domainDnsConfig = {
        cname: isSubdomain ? subdomainRecords == null || (_subdomainRecords$cna = subdomainRecords.cnameRecords) == null ? void 0 : _subdomainRecords$cna[0].value : pointingRecords == null || (_pointingRecords$cnam = pointingRecords.cnameRecord) == null ? void 0 : _pointingRecords$cnam.value,
        aRecord: pointingRecords == null || (_pointingRecords$aRec = pointingRecords.aRecord) == null || (_pointingRecords$aRec = _pointingRecords$aRec.values) == null ? void 0 : _pointingRecords$aRec[0],
        nameServers: nameserverRecord == null || (_nameserverRecord$nsR = nameserverRecord.nsRecord) == null ? void 0 : _nameserverRecord$nsR.values,
        registrar: registrar == null ? void 0 : registrar.name,
        currentNameServers: registrar == null ? void 0 : registrar.nameServers
      };
    }
    return domainSetupConfig;
  } catch (e) {
    var _e$response;
    const statusCode = (e == null || (_e$response = e.response) == null ? void 0 : _e$response.status) ?? (e == null ? void 0 : e.status);
    switch (statusCode) {
      case 404:
        {
          const domainSetupInfoState = await fetchDomainSetupInfo({
            domain: parsedDomain,
            httpClient,
            connectionType,
            withRegistrar
          });
          return domainSetupInfoState;
        }
      default:
        {
          throw e;
        }
    }
  }
};
async function fetchDomainSetupInfo(_ref4) {
  var _subdomainRecords$cna2, _pointingRecords$cnam2, _pointingRecords$aRec2, _nameserverRecord$nsR2;
  let domain = _ref4.domain,
    httpClient = _ref4.httpClient,
    withRegistrar = _ref4.withRegistrar,
    connectionType = _ref4.connectionType;
  const fullAsciiDomain = domain.fullAsciiDomain;
  const isSubdomain = !!domain.subdomain;
  const _await$await$import$D = await (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.query.domainSetupInfo({
      httpClient,
      request: {
        domain: fullAsciiDomain,
        connectionType,
        includeRegistrar: withRegistrar
      }
    }),
    domainSetupInfo = _await$await$import$D.domainSetupInfo;
  const _ref5 = domainSetupInfo ?? {},
    nameserverRecord = _ref5.nameserverRecord,
    pointingRecords = _ref5.pointingRecords,
    subdomainRecords = _ref5.subdomainRecords,
    registrar = _ref5.registrar;
  let connectionStatus = DomainConnectedStatus.CREATE_CONNECT;
  if (isSubdomain && !subdomainRecords) {
    connectionStatus = DomainConnectedStatus.CAN_BE_CONNECTED;
  }
  return {
    domainDnsConfig: {
      cname: isSubdomain ? subdomainRecords == null || (_subdomainRecords$cna2 = subdomainRecords.cnameRecords) == null ? void 0 : _subdomainRecords$cna2[0].value : pointingRecords == null || (_pointingRecords$cnam2 = pointingRecords.cnameRecord) == null ? void 0 : _pointingRecords$cnam2.value,
      aRecord: pointingRecords == null || (_pointingRecords$aRec2 = pointingRecords.aRecord) == null || (_pointingRecords$aRec2 = _pointingRecords$aRec2.values) == null ? void 0 : _pointingRecords$aRec2[0],
      nameServers: nameserverRecord == null || (_nameserverRecord$nsR2 = nameserverRecord.nsRecord) == null ? void 0 : _nameserverRecord$nsR2.values,
      registrar: (registrar == null ? void 0 : registrar.name) ?? Registrar.OTHER,
      currentNameServers: registrar == null ? void 0 : registrar.nameServers
    },
    connectionStatus
  };
}
//# sourceMappingURL=connectedDomainSetupConfig.js.map